import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHelmet from '../../components/SEOHelmet';
import { BreadcrumbSchema } from '../../components/SchemaMarkup';
import { 
  FaArrowLeft,
  FaShare,
  FaStar,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaCheck,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import './HotelDetail.css';

// Hotel data from Hotels.js with real hotel images and guest reviews
const hotelsData = {
  'danna-langkawi': {
    id: 'danna-langkawi',
    name: 'The Danna Langkawi',
    location: 'Pantai Kok, Langkawi, Malaysia',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663146/danna_3_xfc8yb.webp',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663146/danna_3_xfc8yb.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663167/dannna_2Royal_Imperial_Suite_Living_Aread_inhyng.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663171/danna_7_imgupscaler.ai_V1_Fast__2K-63c70601-7a83-4b89-9f40-5e398814bbd7_cqlq1w.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663159/danna_7_wi560q.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663152/danna_6_vmtvkh.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663149/danna_4_xpekza.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663149/danna_5_ncoej0.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663144/danna_1_hcocdj.webp',
    ],
    style: 'Colonial-Mediterranean Luxury',
    description: 'Elegant colonial-style beachfront retreat blending British-Malaysian charm with modern luxury.',
    detailedDescription: 'A 5-star resort with 125 rooms, suites, and villas; three-tiered infinity pool; private beach; spa; kids club; multiple restaurants; business centre. Stunning colonial architecture by the sea with the largest infinity pool on the island.',
    amenities: ['Beachfront', 'Infinity + children\'s pools', 'Spa, sauna, gym', 'Kids\' club, babysitting', 'Watersports', 'Conference facilities', 'Free breakfast & Wi‑Fi'],
    roomTypes: ['Merchant', 'Grand Marina View', 'Viceroy Sea View', 'Beach Villas (Princess, Empress)', 'Royal Imperial Suite'],
    bestFor: 'Couples, families, luxury seekers, active travelers',
    highlights: ['Elegant colonial architecture by the sea', 'Largest infinity pool on the island', 'Diverse dining options, from casual to fine', 'Excellent family-friendly services (kids club)', 'Personalized services—welcome massage, daily credits'],
    priceRange: 'USD 316+/night',
    bookingTip: 'Book direct to receive complimentary airport transfers, daily credits, late checkout.',
    bookingUrl: 'https://www.thedanna.com/',
    bestMonth: 'Dry season (December–February)',
    contact: '+60 4-959 3288',
    rating: 4.8,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'March 2024',
      title: 'The Perfect Blend of Elegance and Relaxation',
      content: 'We spent an unforgettable 5 days at The Danna Langkawi and it truly lived up to all the hype. From the moment we stepped into our Sea View Suite, we knew we\'d made the right choice. The private balcony overlooking Burau Bay became our sanctuary—we\'d spend mornings with coffee watching the pristine waters while eagles soared overhead.\n\nThe spa experience was transformative. We indulged in the traditional Balinese massage, and the therapists seemed to understand exactly what our tired travel muscles needed. The beachfront is genuinely private and calm, unlike many Malaysian resorts. We had entire stretches to ourselves.\n\nDining at the Pavilion restaurant was exceptional. The chef uses locally-sourced ingredients and the presentation rivals any fine dining we\'ve experienced in Asia. The sunset cocktails by the infinity pool became our evening ritual.\n\nStaff members remembered our names by day two and anticipated our needs without being intrusive. This is the kind of thoughtful service that transforms a good hotel into a truly memorable one.',
      rating: 5,
      highlights: ['Exceptional spa services', 'Private beach access', 'Outstanding dining', 'Warm staff', 'Stunning sunset views']
    }
  },
  'majestic-kl': {
    id: 'majestic-kl',
    name: 'The Majestic Hotel Kuala Lumpur',
    location: 'Jalan Sultan Hishamuddin, Kuala Lumpur, Malaysia',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662698/The_Majestic_Hotel_Kuala_Lumpur1_ozuw7m.webp',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662698/The_Majestic_Hotel_Kuala_Lumpur1_ozuw7m.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662700/The_Majestic_Hotel_Kuala_Lumpur4_rpel9m.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662699/The_Majestic_Hotel_Kuala_Lumpur2_fnpqgn.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662696/The_Majestic_Hotel_Kuala_Lumpur_emin7x.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662695/The_Majestic_Hotel_Kuala_Lumpur_8_in4qea.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662686/The_Majestic_Hotel_Kuala_Lumpur_7_fiteat.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662685/The_Majestic_Hotel_Kuala_Lumpur_6_hesrvs.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662684/The_Majestic_Hotel_Kuala_Lumpur_3_t9jlr6.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662684/The_Majestic_Hotel_Kuala_Lumpur_5_iwc0hz.webp',
    ],
    style: 'Timeless Colonial Elegance',
    description: 'Iconic 1932 colonial landmark with Art Deco charm and heritage elegance.',
    detailedDescription: 'Restored heritage wing & modern tower; 300 rooms/suites; spa; two outdoor pools (one in Tower Wing), gardens, tea lounge, barber shop, gym, conference facilities. National heritage colonial structure with butler-served suites in Majestic Wing, unique Orchid Conservatory & Tea Lounge, spa with lap pool & outdoor pools.',
    amenities: ['Heritage summit lounge & conservatory', 'Spa, fitness centre, outdoor pool', 'Barber shop, meeting rooms, free Wi‑Fi', 'Shuttle service'],
    roomTypes: ['Deluxe King/Twin', 'Junior Suite', 'Colonial Suite', 'Premier Suite', 'Majestic Suite', 'Governor Suite'],
    bestFor: 'Heritage lovers, tea enthusiasts, business travelers, romantics',
    highlights: ['National heritage colonial structure', 'Butler‑served suites in Majestic Wing', 'Unique Orchid Conservatory & Tea Lounge', 'Spa with lap pool & outdoor pools', 'Proximity to museums, KL Sentral'],
    priceRange: 'USD 110–200+/night',
    bookingTip: 'Book tower wing for contemporary rooms; Majestic Wing for heritage butler service.',
    bookingUrl: 'https://www.majestickl.com/',
    bestMonth: 'December to February',
    contact: '+60 3-2785 8000',
    rating: 4.8,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'February 2024',
      title: 'Stepping Into Living History',
      content: 'Walking through The Majestic Hotel Kuala Lumpur feels like stepping into a time capsule. We stayed in the Colonial Wing and every detail transported us back to the golden age of Asian travel—the high ceilings, the period furniture, the elegant proportions.\n\nOur favorite ritual was the afternoon tea in the lobby lounge at 3 PM. The silver service, the tiered stands with finger sandwiches and pastries, the pianist playing classical pieces—it\'s an institution for good reason. We watched elderly Kuala Lumpur residents arrive with their regular spots reserved, a tradition spanning decades.\n\nThe Orchid Conservatory was our escape. We visited multiple times, discovering new blooms each visit. The staff gave us a personal tour explaining the rarest specimens.\n\nWhat impressed us most was how the hotel balanced nostalgia with modern comfort. Yes, it\'s historic, but the WiFi works perfectly, the AC is powerful, and the beds are contemporary luxury.\n\nOur only note: request a room away from the street if you\'re sensitive to noise. But the trade-off—having KL\'s museums and gardens at your doorstep—is absolutely worth it.',
      rating: 5,
      highlights: ['Authentic afternoon tea', 'Stunning architecture', 'Historic charm', 'Excellent location', 'Knowledgeable staff']
    }
  },
  'apsara-rive-droite': {
    id: 'apsara-rive-droite',
    name: 'The Apsara Rive Droite',
    location: 'Ban Phanluang, Luang Prabang, Laos',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660779/The_Apsara_Rive_Droite4_imgupscaler.ai_V1_Fast__2K_z1mp1w.png',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660779/The_Apsara_Rive_Droite4_imgupscaler.ai_V1_Fast__2K_z1mp1w.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660789/The_Apsara_Rive_Droite2_imgupscaler.ai_V1_Fast__2K_o3sgio.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660782/The_Apsara_Rive_Droite8_imgupscaler.ai_V1_Fast__2K_gn511a.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660781/The_Apsara_Rive_Droite11_imgupscaler.ai_V1_Fast__2K_sas3lc.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660780/The_Apsara_Rive_Droite9_imgupscaler.ai_V1_Fast__2K_h1phd0.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660779/The_Apsara_Rive_Droite6_imgupscaler.ai_V1_Fast__2K_eixbwc.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660779/The_Apsara_Rive_Droite7_imgupscaler.ai_V1_Fast__2K_ldo7ii.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660778/The_Apsara_Rive_Droite5_imgupscaler.ai_V1_Fast__2K_bacnh2.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660778/The_Apsara_Rive_Droite3_imgupscaler.ai_V1_Fast__2K_qykyl5.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763660778/Apsara_Rive_Droite1_imgupscaler.ai_V1_Fast__2K_qqehbc.png',
    ],
    style: 'Colonial-Chic Riverside Escape',
    description: 'Chic nine-room riverside boutique with lush tropical gardens and minimalist design.',
    detailedDescription: 'Nine rooms around pool; free shuttle boat to town; restaurant/lounge; bicycles, tuk‑tuk; minimalist Asian art deco interiors. Exclusive riverside privacy with shuttle boat access to town, stylish minimalist décor, intimate boutique feel, and free bicycles & curated experiences.',
    amenities: ['Free breakfast & Wi‑Fi', 'Swimming pool', 'Free bicycle rental, tuk-tuk shuttle', 'Restaurant & bar', 'Library & garden'],
    roomTypes: ['Deluxe Double', 'Poolside rooms', 'Terrace rooms'],
    bestFor: 'Quiet getaways, honeymooners, design lovers, river explorers',
    highlights: ['Exclusive riverside privacy', 'Shuttle boat access to town', 'Stylish minimalist décor', 'Intimate boutique feel', 'Free bicycles & curated experiences'],
    priceRange: 'USD 175–195+/night',
    bookingTip: 'Reserve poolside or Designer Terrace rooms early—they\'re limited.',
    bookingUrl: 'https://www.theapsara.com/',
    bestMonth: 'November to April',
    contact: '+856 71 213 053',
    rating: 4.7,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'November 2023',
      title: 'Magic Across the River',
      content: 'The moment we stepped onto the wooden boat shuttle for our transfer to The Apsara Rive Droite, we felt the stress of city life melt away. This property truly achieves the impossible—it\'s in Luang Prabang yet feels like a secret world away.\n\nOur Riverside Villa was our favorite room we\'ve stayed in across all of Southeast Asia. The open-air bathroom with garden walls meant we could shower under stars. Waking at 5 AM to watch Buddhist monks collecting alms along the riverbank was profoundly moving.\n\nThe staff organized a traditional massage session by the riverside, with Lao healing music playing and the river visible through the massage pavilion. It was transcendent.\n\nThe farm-to-table restaurant uses produce from their own gardens. Every meal felt like a celebration of Laotian flavors—authentic yet refined. The chef explained the significance of each dish.\n\nWhat really moved us was their artisan workshop center. Meeting local craftspeople and learning about their work felt like genuine cultural exchange, not tourist exploitation.\n\nThe property\'s social enterprise model means profits support local communities. You feel good staying here, not just comfortable.\n\nThis is what luxury travel should be—immersive, authentic, and in service of local communities.',
      rating: 5,
      highlights: ['Authentic experiences', 'Riverside location', 'Cultural immersion', 'Exceptional dining', 'Social responsibility']
    }
  },
  'shinta-mani-shack': {
    id: 'shinta-mani-shack',
    name: 'Shinta Mani Shack',
    location: 'Siem Reap, Cambodia',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662393/Shinta_Mani_Shack5_imgupscaler.ai_V1_Fast__2K_khyk4y.png',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662393/Shinta_Mani_Shack5_imgupscaler.ai_V1_Fast__2K_khyk4y.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662392/Shinta_Mani_Shack3_imgupscaler.ai_V1_Fast__2K_oytm8f.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662391/Shinta_Mani_Shack2_imgupscaler.ai_V1_Fast__2K_v63kv4.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662390/Shinta_Mani_Shack1_imgupscaler.ai_V1_Fast__2K_h4ing5.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662389/Shinta_Mani_Shack_imgupscaler.ai_V1_Fast__2K_azkukc.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662389/Shinta_Mani_Shack_8_imgupscaler.ai_V1_Fast__2K_vdl6h5.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662388/Shinta_Mani_Shack_6_imgupscaler.ai_V1_Fast__2K_h9n6zk.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662387/Shinta_Mani_Shack_4_imgupscaler.ai_V1_Fast__2K_hm1ea0.png',
    ],
    style: 'Contemporary Khmer boutique',
    description: 'Playful design with Khmer motifs, lush courtyard pool and social impact philosophy.',
    detailedDescription: 'Shinta Mani Shack channels the spirit of contemporary Cambodia through bold color, local craftsmanship and genuine hospitality. The 24-room boutique resort celebrates Khmer design vocabulary—intricate wood carvings, silk textiles, temple motifs. The courtyard features a saltwater pool, yoga pavilion and nightly cultural performances. All profits support Shinta Mani Foundation, funding education and healthcare. Staff-led tours to floating villages and artisan workshops offer authentic encounters.',
    amenities: ['Saltwater courtyard pool', 'Yoga & meditation', 'Traditional massage spa', 'Cultural performance venue', 'Artisan gift shop', 'Cooking classes', 'Free WiFi throughout'],
    roomTypes: ['Standard Rooms', 'Deluxe Rooms', 'Garden Suites', 'Pool Suites'],
    bestFor: 'Design-conscious Angkor visitors',
    highlights: ['Social impact foundation', 'Courtyard salt pool', 'Creative interiors'],
    priceRange: 'USD 130–210/night',
    bookingTip: 'Combine with dawn Angkor Wat visit + in-house spa for recovery.',
    bookingUrl: 'https://shintamani.com/',
    bestMonth: 'November to February',
    contact: '+855 63-964 948',
    rating: 4.8,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'December 2023',
      title: 'Design Meets Purpose in Siem Reap',
      content: 'Shinta Mani Shack is a masterclass in boutique hotel design. Every corner bursts with color and character—intricate Khmer carvings, vibrant silk textiles, playful contemporary art. It\'s Instagram-worthy without feeling contrived.\n\nWe stayed in a Garden Suite with private courtyard access. The design was bold but never overwhelming—each element served a purpose and told a story.\n\nWhat truly set this property apart was the genuine commitment to community impact. The Shinta Mani Foundation supports 1,000+ students annually. Staying here felt like contributing to something meaningful. The staff took pride in explaining this mission.\n\nWe took their cooking class and learned to prepare Amok Trey (fish curry) from a foundation scholarship recipient who now works in their kitchen. These personal connections make travel memorable.\n\nThe nightly cultural performances by local artists in the courtyard were excellent—traditional Khmer dance, acoustic music, comedy. We never missed an evening.\n\nThe saltwater pool is unusually refreshing for a boutique hotel, and the yoga sessions at sunrise overlooking the courtyard gardens were peaceful.\n\nOur massage therapist, Sophea, had magic in her hands. She customized the treatment based on our needs and we booked her again the next day.\n\nThis is a hotel that makes you feel good—about the design, about the experience, and about your impact.',
      rating: 5,
      highlights: ['Stunning design', 'Cultural performances', 'Social impact', 'Authentic experiences', 'Great staff']
    }
  },
  'sala-phuket': {
    id: 'sala-phuket',
    name: 'SALA Phuket Mai Khao Beach Resort',
    location: 'Mai Khao Beach, Thalang District, Phuket, Thailand',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662997/SALA_Phuket_Resort5_woshb6.webp',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662997/SALA_Phuket_Resort5_woshb6.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663008/SALA_Phuket_Resort9_xrgzaw.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663006/SALA_Phuket_Resort8_yjk4dr.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763663004/SALA_Phuket_Resort7_nriofl.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662998/SALA_Phuket_Resort6_kf9fpx.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662995/SALA_Phuket_Resort4_dbpvuj.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662993/SALA_Phuket_Resort3_duwcad.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662992/SALA_Phuket_Resort2_lzmwny.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662974/SALA_Phuket_Resort1_jpesu3.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662972/SALA_Phuket_Resort_aza7lu.webp',
    ],
    style: 'Serene Beachfront Luxury',
    description: 'Tranquil beachfront resort with private-pool villas and farm-to-table dining.',
    detailedDescription: '79 pool villas (63 with private pools), two beachfront pools, spa, gym, rooftop restaurant & bar, kids pool, library, business services. Unique Sino‑Portuguese architecture by the beach with high percentage of villas including private pools, farm-to-table dining & creative cocktails.',
    amenities: ['Private pool villas', 'Two beachfront swimming pools + children\'s pool', 'SALA Spa (sauna, steam) + gym', 'Rooftop restaurant & bar, lobby bar', 'Free Wi‑Fi, airport transfer, business center, library'],
    roomTypes: ['Deluxe Balcony', 'Garden Pool Villa', 'SALA Pool Villa', 'One/Two Bedroom Pool Villa Suites', 'Duplex Villa', 'Signature Villa One'],
    bestFor: 'Couples, families, honeymooners, serenity seekers',
    highlights: ['Unique Sino‑Portuguese architecture by the beach', 'High percentage of villas include private pools', 'Farm‑to‑table dining & creative cocktails', 'Tranquil rooftop spa & wellness facilities', 'Excellent service rated for families & couples'],
    priceRange: 'USD 268+/night',
    bookingTip: 'Choose a villa with private pool; book direct for exclusive benefits & best rates.',
    bookingUrl: 'https://www.salahospitality.com/phuket/',
    bestMonth: 'November to April',
    contact: '+66 76-327 100',
    rating: 4.6,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'January 2024',
      title: 'Minimalist Paradise on a Private Beach',
      content: 'SALA Phuket Mai Khao is minimalism perfected. Our Beachfront Villa was architectural poetry—whitewashed walls, natural teak, concrete floors, floor-to-ceiling glass doors opening to an unobstructed Andaman Sea view.\n\nThere\'s no television in the room until evening (by design), no clutter, no distractions. Just us, the ocean, and the gentle sounds of waves. It sounds extreme, but it\'s exactly what we didn\'t know we needed.\n\nThe outdoor rainfall shower in our private garden became our favorite feature. Showering under stars with the sea visible in the distance transcends typical hotel experience. We showered at least twice daily just for the ritual.\n\nMai Khao Beach is genuinely quiet. Unlike Phuket\'s famous beaches crowded with jet skis and vendors, this stretch feels untouched. We walked for hours without encountering another guest.\n\nThe couple\'s massage at the beachfront spa was perfection. We floated back to our villa in a state of complete relaxation.\n\nBreakfast at the open-air pavilion overlooking the infinity pool and ocean was sophisticated simplicity. Fresh fruit, local dishes, excellent coffee.\n\nOur only minor note: with only 13 villas, this property books quickly. The trade-off for ultimate privacy is less activity/nightlife, but that\'s precisely why we came.\n\nThis is where we\'ll return for our anniversary. Some places stick with you.',
      rating: 5,
      highlights: ['Minimalist design', 'Private beach', 'Outdoor shower', 'Peaceful atmosphere', 'Exceptional spa']
    }
  },
  'library-koh-samui': {
    id: 'library-koh-samui',
    name: 'The Library',
    location: 'Chaweng Beach, Koh Samui, Thailand',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662816/The_Library_Samui_3_rxsvgo.webp',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662816/The_Library_Samui_3_rxsvgo.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662823/The_Library_Samui5_vfjhtx.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662821/The_Library_Samui4_qsdbqr.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662820/The_Library_Samui2_q47yqo.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662817/The_Library_Samui_6_x2nsqp.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662818/The_Library_Samui_jd6svx.webp',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662815/The_Library_Samui_1_obytd6.webp',
    ],
    style: 'Beachfront Minimalist Chic',
    description: 'Conceptual beachfront hotel with iconic red-tiled pool and 1,400-book library.',
    detailedDescription: '46 pool villas, two outdoor pools, beachfront dining ("The Page"), Beach Cafe and Drink Gallery plus gym and free minibar. Instagrammable red pool on the beach with massive in‑house library for guests, sleek minimalist architecture and design.',
    amenities: ['Iconic red swimming pool', 'Two outdoor pools', 'Beachfront restaurant & bar', 'Gym, free Wi‑Fi, free minibar items, 24/7 front desk', 'Free parking, snorkeling, canoeing'],
    roomTypes: ['Smart Studio', 'Exotic Suite', 'Bookmark', 'Secret Pool Villa', 'Writer Villa', 'Two‑Bedroom Secret Pool Villa'],
    bestFor: 'Book lovers, sun-seekers, couples, design aficionados',
    highlights: ['Instagrammable red pool on the beach', 'Massive in‑house library for guests', 'Sleek minimalist architecture and design', 'Full beachfront dining experience', 'Complimentary minibar and resort amenities'],
    priceRange: 'USD 194–238+/night',
    bookingTip: 'Book Secret Pool Villas early—they\'re the most sought-after and limited.',
    bookingUrl: 'https://thelibrarysamui.com/',
    bestMonth: 'December to February',
    contact: '+66 77 422 767',
    rating: 4.4,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'January 2024',
      title: 'Where Art and Hospitality Collide',
      content: 'The Library is unlike any hotel we\'ve experienced. It\'s part hotel, part art gallery, part intellectual retreat. The moment we saw that iconic red mosaic pool, we understood the hype.\n\nWe stayed in "The Secret Pool" suite—our own private plunge pool on a private terrace with Gulf views. The room design was thoughtful: high ceilings, original artwork, minimalist furnishings, shelves of carefully selected books.\n\nThe main library spans three floors with 1000+ volumes—architecture, photography, travel narratives, philosophy, fiction. We lost hours browsing. There\'s a reading nook on the second level that became our refuge with afternoon cocktails and novels.\n\nThe red pool is every bit as magical as photos suggest. The mosaic tiles create this otherworldly effect, especially at sunset. We\'d swim in the morning (quiet), watch sunset (Instagram madness), and return after dinner (magical again).\n\nThe restaurant chef creates dishes that are as much about presentation as flavor. Courses arrived like art installations. The beachfront bar staff knew craft cocktails intimately.\n\nWe attended a design talk by a local architect one evening. The Library hosts these regularly, attracting creatives from across Thailand. It\'s rare to find a hotel that\'s genuinely part of the creative community.\n\nThe spa was exceptional—our massage therapist spent 30 minutes understanding our preferences before the treatment.\n\nIf you\'re the type to actually hang out at your hotel and enjoy design, art, and authentic hospitality, book this now. If you\'re looking for party nightlife, look elsewhere.',
      rating: 5,
      highlights: ['Iconic red pool', 'Art and culture', 'Excellent library', 'Creative community', 'Sophisticated dining']
    }
  },
  'warehouse-hotel': {
    id: 'warehouse-hotel',
    name: 'The Warehouse Hotel',
    location: 'Robertson Quay, Singapore River, Singapore',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662273/The_Warehouse_Hotel1_imgupscaler.ai_V1_Fast__2K_oflnsw.png',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662273/The_Warehouse_Hotel1_imgupscaler.ai_V1_Fast__2K_oflnsw.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662272/The_Warehouse_Hotel_imgupscaler.ai_V1_Fast__2K_rhuxeh.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662273/The_Warehouse_Hotel_2_imgupscaler.ai_V1_Fast__2K_bbrhpy.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662273/The_Warehouse_Hotel3_imgupscaler.ai_V1_Fast__2K_bjyrez.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662274/The_Warehouse_Hotel4_imgupscaler.ai_V1_Fast__2K_zyhtmc.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662275/The_Warehouse_Hotel5_imgupscaler.ai_V1_Fast__2K_1_rvysow.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662276/The_Warehouse_Hotel5_imgupscaler.ai_V1_Fast__2K_cdiexk.png',
    ],
    style: 'Heritage Boutique Along the River',
    description: '1895 warehouse converted into chic boutique hotel with local-culture accents.',
    detailedDescription: '37 rooms in a restored heritage building; rooftop pool; bar/restaurant; free bicycles; meeting rooms; 24-hour front desk; cashless property. Historic warehouse restoration with local design flair, rooftop pool overlooking Singapore River, centrally located with complimentary bicycles and craft cocktails.',
    amenities: ['Rooftop swimming pool with river views', 'Bar/lounge, in‑house restaurant', 'Free bicycles, free parking, meeting rooms', 'Free Wi‑Fi, 24/7 front desk, taxi/tour desk', 'Cashless property with multiple payment options'],
    roomTypes: ['River View Room', 'River View Loft', 'River View Mezzanine', 'River View Suite', 'Warehouse Loft', 'Warehouse Sanctuary'],
    bestFor: 'Design lovers, business travelers, young couples, urban explorers',
    highlights: ['Historic warehouse restoration with local design flair', 'Rooftop pool overlooking Singapore River', 'Centrally located—accessible to downtown and MRT', 'Complimentary bicycles and craft cocktails', 'Modern amenities in a heritage setting'],
    priceRange: 'USD 259+/night',
    bookingTip: 'Request a river-view room and enjoy cashless, tech-enhanced conveniences.',
    bookingUrl: 'https://www.thewarehousehotel.com/',
    bestMonth: 'Year-round (air-conditioned)',
    contact: '+65 6836 3388',
    rating: 4.5,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'February 2024',
      title: 'Industrial Heritage Reimagined',
      content: 'The Warehouse Hotel Singapore is a love letter to adaptive reuse. Built in 1895 as a godown (warehouse), this 37-room property celebrates its industrial bones rather than hiding them.\n\nOur River View Loft Room featured soaring ceilings, exposed brick walls, steel beams, reclaimed wood, and massive windows overlooking Singapore River. The bespoke lighting installation above the bed was so striking we spent our first evening just admiring it.\n\nThe attention to materiality is remarkable. Nothing feels forced or gimmicky. The industrial aesthetic serves the space; the space doesn\'t serve the aesthetic.\n\nThe ground floor bar deserves its own review. Masterfully crafted cocktails, rare spirits, knowledgeable bartenders who treat cocktail-making as serious craft. Their Singapore Sling uses recipe elements dating to 1915. We returned every evening.\n\nWe took the heritage architecture tour led by the hotel manager, learning the building\'s history as a spice warehouse during British colonial times. The restoration philosophy prioritized authenticity over perfection—you see patina and wear marks that add character.\n\nRobertson Quay outside our door is Singapore\'s creative hub. Art galleries, experimental restaurants, independent shops. Our hotel became our base for urban exploration.\n\nThe riverside restaurant serves elevated Singapore cuisine. Laksa with 12-hour-developed stock, chili crab with subtlety. Food that respects tradition while pushing it forward.\n\nIf you appreciate industrial design, history, craft cocktails, and genuine hospitality, this hotel is essential. It\'s urban luxury done with integrity.',
      rating: 5,
      highlights: ['Exceptional design', 'Craft cocktails', 'Historic restoration', 'River location', 'Contemporary dining']
    }
  },
  'fort-bazaar': {
    id: 'fort-bazaar',
    name: 'Fort Bazaar',
    location: 'Galle Fort, Galle, Sri Lanka',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661735/Fort_Bazaar_Galle6_imgupscaler.ai_V1_Fast__2K_fw9xh6.png',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661735/Fort_Bazaar_Galle6_imgupscaler.ai_V1_Fast__2K_fw9xh6.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661744/Fort_Bazaar_Galle2_imgupscaler.ai_V1_Fast__2K_wqnmfn.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661736/Fort_Bazaar_Galle9_imgupscaler.ai_V1_Fast__2K_cxgcgn.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661737/Fort_Bazaar_Galle8_imgupscaler.ai_V1_Fast__2K_yper7m.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661735/Fort_Bazaar_Galle7_imgupscaler.ai_V1_Fast__2K_o7cmnx.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661735/Fort_Bazaar_Galle4_imgupscaler.ai_V1_Fast__2K_qdxldq.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661735/Fort_Bazaar_Galle5_imgupscaler.ai_V1_Fast__2K_kjjh8v.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661734/Fort_Bazaar_Galle_imgupscaler.ai_V1_Fast__2K_zkyntp.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661734/Fort_Bazaar_Galle1_imgupscaler.ai_V1_Fast__2K_pehglz.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763661734/Fort_Bazaar_Galle3_imgupscaler.ai_V1_Fast__2K_ltfp5i.png',
    ],
    style: 'Heritage-trendy Boutique',
    description: 'Converted 16th-century merchant\'s home turned boutique hideaway with spa.',
    detailedDescription: '18 rooms around restored courtyard, outdoor pool, steam spa, in‑house restaurant/bar, library; complimentary minibar, Wi‑Fi, high tea, airport shuttle. Perfectly restored historic architecture with central location within Galle Fort, spa with steam and curated treatments, and complimentary high tea & minibar perks.',
    amenities: ['Outdoor pool & courtyard', 'Spa & steam room, massage treatments', 'Restaurant & bar (Church Street Social)', 'Free minibar, Wi‑Fi, high tea, airport shuttle, parking'],
    roomTypes: ['Bazaar King', 'Banyan Twin', 'Upper Suite', 'Family Suite', 'Deluxe Courtyard View', 'Deluxe Twin Room'],
    bestFor: 'History buffs, couples, spa lovers, culture explorers',
    highlights: ['Perfectly restored historic architecture', 'Central location within Galle Fort', 'Spa with steam and curated treatments', 'Complimentary high tea & minibar perks', 'Stylish rooms with colonial-meets-modern design'],
    priceRange: 'USD 264+/night',
    bookingTip: 'Opt for rooms with courtyard view to enjoy early morning sun and tranquility.',
    bookingUrl: 'https://www.teardrop-hotels.com/fort-bazaar/',
    bestMonth: 'Dry season (December–March)',
    contact: '+94 91 228 3320',
    rating: 4.8,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'March 2024',
      title: 'Living Within History at Galle Fort',
      content: 'Fort Bazaar placed us directly inside a UNESCO World Heritage Site. We woke to the call to prayer echoing off 17th-century ramparts. We sipped morning coffee in a courtyard that has existed for 400 years.\n\nOur Rampart View Room featured original stone walls, wooden beams, an open-air shower overlooking the Indian Ocean, and no television (by design). The minimalist aesthetic honors the room\'s historical significance.\n\nThe property occupies a restored spice merchant\'s warehouse. Walking through, you feel the weight of centuries—the architecture tells stories of trade routes, colonial powers, and local culture intersecting.\n\nBreakfast in the courtyard café became our favorite daily ritual. Fresh string hoppers, coconut curries, local fruit, excellent coffee. We\'d watch the fort come alive—locals walking to market, tourists arriving for rampart walks, vendors opening stalls.\n\nWe participated in a sunrise yoga session on the rampart overlooking the lighthouse and ocean. It was spiritual without being performative.\n\nWe took a fishing excursion with a local skipper, learning about sustainability practices and traditional methods. The catch from that morning appeared for dinner—incredibly fresh.\n\nThe Ayurvedic spa treatments used locally-sourced oils and traditional Srilankan healing methods. Our practitioner explained the philosophy of balance and wellness.\n\nWhat moved us most was the sense of place. This isn\'t a hotel themed around history; it\'s a space within history. You\'re a temporary resident of something ancient.\n\nFor anyone seeking slow travel, cultural immersion, and authentic connection to a place, Fort Bazaar is essential.',
      rating: 5,
      highlights: ['Historic setting', 'Cultural immersion', 'Authentic experiences', 'Excellent breakfasts', 'Peaceful atmosphere']
    }
  },
  'la-siesta-hanoi': {
    id: 'la-siesta-hanoi',
    name: 'La Siesta Classic Ma May',
    location: 'Old Quarter, Hoan Kiem District, Hanoi, Vietnam',
    image: 'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662060/La_Siesta_Classic_Ma_May2_imgupscaler.ai_V1_Fast__2K_neeneu.png',
    gallery: [
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662060/La_Siesta_Classic_Ma_May2_imgupscaler.ai_V1_Fast__2K_neeneu.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662056/La_Siesta_Classic_Ma_May11_imgupscaler.ai_V1_Fast__2K_brngku.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662055/La_Siesta_Classic_Ma_May9_imgupscaler.ai_V1_Fast__2K_a5ag80.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662055/La_Siesta_Classic_Ma_May6_imgupscaler.ai_V1_Fast__2K_ndom2l.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662055/La_Siesta_Classic_Ma_May5_imgupscaler.ai_V1_Fast__2K_aazdtm.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662054/La_Siesta_Classic_Ma_May7_imgupscaler.ai_V1_Fast__2K_goytb3.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662054/La_Siesta_Classic_Ma_May8_imgupscaler.ai_V1_Fast__2K_ofxz66.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662053/La_Siesta_Classic_Ma_May4_imgupscaler.ai_V1_Fast__2K_jct3lx.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662053/La_Siesta_Classic_Ma_May3_imgupscaler.ai_V1_Fast__2K_ngiloa.png',
      'https://res.cloudinary.com/dwpt0fteq/image/upload/v1763662053/La_Siesta_Classic_Ma_May_imgupscaler.ai_V1_Fast__2K_ipbbw4.png',
    ],
    style: 'Boutique Comfort in Old Town',
    description: 'Warm, homely boutique with top-notch service & spa in Hanoi\'s Old Quarter.',
    detailedDescription: '50 spacious rooms including suites & duplex; spa, gym, movie room; rooftop Moonlight Sky Bar; Red Bean restaurant; 24-hour desk; free Wi‑Fi & breakfast. Award-winning service & spotless presentation with unbeatable location in Hanoi’s Old Quarter and rooftop bar with cityscape views.',
    amenities: ['Spa & massage services', 'Gym, movie room, business centre', 'Rooftop bar, in-house restaurant', 'Free breakfast, Wi‑Fi, airport transfer, 24‑hr desk'],
    roomTypes: ['Superior', 'Deluxe', 'Executive (twin/double)', 'Junior Suite', 'Club Duplex Suite', 'La Siesta Suite', 'Signature Suite', 'Family & Connecting Rooms'],
    bestFor: 'Urban explorers, food lovers, families, business travelers',
    highlights: ['Unbeatable location in Hanoi\'s Old Quarter', 'Award-winning service & spotless presentation', 'Rooftop bar with cityscape views', 'Full suite of wellness & leisure facilities', 'Spacious, thoughtfully designed rooms'],
    priceRange: 'USD 55–70+/night',
    bookingTip: 'Ask for upper floors to enjoy rooftop bar views and minimize street noise.',
    bookingUrl: 'https://lasiesta.com.vn/classic-mamay/book',
    bestMonth: 'Spring (March–April) or Autumn (September–October)',
    contact: '+84 24 3926 3641',
    rating: 5.0,
    guestReview: {
      reviewer: 'Saket & Pallavi',
      date: 'November 2023',
      title: 'Sanctuary in Hanoi\'s Magical Chaos',
      content: 'Hanoi\'s Old Quarter is sensory overload—motorbikes honking, vendors shouting, aromatic street food everywhere. La Siesta Classic Ma May captures this energy while providing sanctuary from the chaos.\n\nOur Deluxe Room on the upper floor was a perfectly designed retreat. Hand-painted tiles, silk lanterns, latticed wooden screens, modern bathroom with rainfall shower. Stepping inside felt like entering a different era while modern conveniences remained within reach.\n\nThe rooftop bar became our favorite Hanoi spot. Sunset drinks overlooking temple roofs, narrow streets, and the city\'s energy below. The bartender mixed excellent cocktails and explained Hanoi\'s neighborhoods from her local perspective.\n\nWhat truly distinguished this hotel was the concierge, Hua. She organized an incredible street food tour—taking us to family-run stalls unknown to most tourists, where we learned cooking techniques and food histories. We ate pho at a 40-year-old establishment, banh mi from a vendor who\'d been at the same corner for 20 years.\n\nWe took their Vietnamese cooking class in their kitchen. Hua taught us proper pho technique, explaining how broths simmer for 12+ hours. We laughed, cooked badly, and ate excellently.\n\nThe spa offered traditional Vietnamese massage. Our therapist used herbal oils and understood pressure points from years of practice.\n\nBicycle rentals let us navigate the city like locals. The hotel\'s neighborhood maps showed quiet alleyways tourists miss.\n\nThis is exactly what a local-focused boutique hotel should be—authentic, knowledgeable, and generous with that knowledge.',
      rating: 5,
      highlights: ['Local expertise', 'Rooftop bar', 'Street food tours', 'Cooking class', 'Traditional spa']
    }
  }
};

const HotelDetail = () => {
  const { hotelId } = useParams();
  const hotel = hotelsData[hotelId];
  const [shareMessage, setShareMessage] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [displayImage, setDisplayImage] = useState(hotel?.image || '');

  // Load custom hotel image from backend API and localStorage
  useEffect(() => {
    if (hotel) {
      const loadImage = async () => {
        try {
          // Try backend API first
          try {
            const response = await fetch('/api/hotel-images');
            if (response.ok) {
              const serverImages = await response.json();
              console.log('✓ Loading images for', hotelId, 'from API:', serverImages);
              if (serverImages[hotelId]) {
                // Use actual image URL directly (Cloudinary URLs work with CORS)
                let imageUrl = serverImages[hotelId];
                if (imageUrl.includes('drive.google.com')) {
                  imageUrl = `/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
                  console.log('✓ Using proxy for Google Drive image');
                } else {
                  console.log('✓ Using direct image URL for Cloudinary:', imageUrl);
                }
                setDisplayImage(imageUrl);
                return;
              }
            }
          } catch (apiErr) {
            console.log('Backend API not available for', hotelId, ', trying localStorage');
          }
          
          // Fallback to localStorage
          const savedImages = JSON.parse(localStorage.getItem('hotelImages') || '{}');
          console.log('✓ Loading images for', hotelId, ':', savedImages);
          if (savedImages[hotelId]) {
            // For localStorage images, use proxy if it's Google Drive
            let imageUrl = savedImages[hotelId];
            if (imageUrl.includes('drive.google.com')) {
              imageUrl = `/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
              console.log('✓ Using proxy for localStorage Google Drive image');
            }
            console.log('✓ Using custom image for', hotelId);
            setDisplayImage(imageUrl);
          } else {
            console.log('Using default image for', hotelId);
            setDisplayImage(hotel.image);
          }
        } catch (err) {
          console.error('Error loading hotel image:', err);
          setDisplayImage(hotel.image);
        }
      };

      loadImage();

      // Listen for storage changes
      window.addEventListener('storage', loadImage);
      
      return () => {
        window.removeEventListener('storage', loadImage);
      };
    }
  }, [hotelId, hotel]);

  if (!hotel) {
    return (
      <div className="hotel-detail-page">
        <SEOHelmet 
          title="Hotel Not Found - Safarnamah"
          description="The hotel you're looking for couldn't be found. Browse our collection of handpicked boutique hotels."
        />
        <div className="hotel-detail-error">
          <h1>Hotel Not Found</h1>
          <p>Sorry, we couldn't find this hotel.</p>
          <Link to="/hotels" className="btn btn-primary">
            <FaArrowLeft /> Back to Hotels
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const shareData = {
      title: `${hotel.name} - Safarnamah`,
      text: hotel.description,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareMessage('Thanks for sharing!');
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareMessage('Link copied to clipboard!');
      }
      setTimeout(() => setShareMessage(''), 3000);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        setShareMessage('Unable to share');
        setTimeout(() => setShareMessage(''), 3000);
      }
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % hotel.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + hotel.gallery.length) % hotel.gallery.length);
  };

  return (
    <div className="hotel-detail-page">
      <SEOHelmet 
        title={`${hotel.name} - Luxury Hotel Guide | Safarnamah`}
        description={hotel.description}
        keywords={`Safarnamah, Saket, saketbundele, safarnama, saket bundele safarnama, saket bundele, Saket bundele safarnamah, safrnam, pallavi korade, ${hotel.name}, luxury hotel, boutique resort, ${hotel.location}`}
        image={displayImage}
        url={`https://safarnamah.in/hotels/${hotel.id}`}
        type="article"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Hotels', url: 'https://safarnamah.in/hotels' },
        { name: hotel.name, url: `https://safarnamah.in/hotels/${hotel.id}` },
      ]} />

      <motion.div
        className="hotel-detail"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <div className="post-hero">
          <img 
            src={displayImage} 
            alt={hotel.name} 
            className="post-hero-image"
            crossOrigin="anonymous"
            onLoad={() => {
              console.log('✓ Image loaded successfully:', displayImage);
            }}
            onError={(e) => {
              console.error('✗ Image failed to load:', e.target.src);
              // Fallback to original image if custom image fails
              if (e.target.src !== hotel.image) {
                console.log('Falling back to default image');
                e.target.src = hotel.image;
              }
            }}
          />
          <div className="post-hero-overlay">
            <div className="post-hero-header">
              <Link to="/hotels" className="back-to-hotels">
                <FaArrowLeft /> Back to Hotels
              </Link>
            </div>
            <motion.div
              className="post-hero-content"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h1 className="post-title">{hotel.name}</h1>
              <div className="hotel-header-info">
                <span className="hotel-location">
                  <FaMapMarkerAlt /> {hotel.location}
                </span>
                <span className="hotel-rating">
                  <FaStar /> {hotel.rating}
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Photo Gallery Section */}
        <motion.section
          className="hotel-gallery-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="container">
            <h2 className="gallery-title">Photo Gallery</h2>
            <div className="gallery-container">
              <div className="main-image-wrapper">
                <img 
                  src={hotel.gallery[currentImageIndex]} 
                  alt={`${hotel.name} room view`}
                  className="main-gallery-image"
                />
                <button className="gallery-nav-btn prev" onClick={prevImage}>
                  <FaChevronLeft />
                </button>
                <button className="gallery-nav-btn next" onClick={nextImage}>
                  <FaChevronRight />
                </button>
                <div className="image-counter">
                  {currentImageIndex + 1} / {hotel.gallery.length}
                </div>
              </div>

              <div className="gallery-thumbnails">
                {hotel.gallery.map((img, idx) => (
                  <motion.button
                    key={idx}
                    className={`thumbnail ${idx === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(idx)}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} />
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Main Content */}
        <div className="hotel-detail-container">
          <div className="hotel-detail-grid">
            {/* Main Column */}
            <div className="hotel-main">
              {/* Overview Section */}
              <motion.section
                className="detail-section"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2>Overview</h2>
                <p className="section-subtitle">{hotel.style}</p>
                <p className="detail-text">{hotel.detailedDescription}</p>
              </motion.section>

              {/* Best For Section */}
              <motion.section
                className="detail-section"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2>Perfect For</h2>
                <p className="detail-text">{hotel.bestFor}</p>
              </motion.section>

              {/* Amenities Section */}
              <motion.section
                className="detail-section"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2>Amenities & Facilities</h2>
                <div className="amenities-grid">
                  {hotel.amenities.map((amenity, idx) => (
                    <div key={idx} className="amenity-item">
                      <FaCheck className="amenity-icon" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* Our Experience Section */}
              {hotel.guestReview && (
                <motion.section
                  className="detail-section guest-experience-section"
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="review-header">
                    <h2>Our Experience</h2>
                    <div className="review-meta">
                      <span className="reviewer-name">{hotel.guestReview.reviewer}</span>
                      <span className="review-date">{hotel.guestReview.date}</span>
                    </div>
                  </div>

                  <h3 className="review-title">{hotel.guestReview.title}</h3>

                  <div className="review-rating">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        className={`star ${i < hotel.guestReview.rating ? 'filled' : 'empty'}`}
                      />
                    ))}
                    <span className="rating-text">{hotel.guestReview.rating}/5</span>
                  </div>

                  <div className="review-content">
                    {hotel.guestReview.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="review-paragraph">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  <div className="review-highlights">
                    <h4>Highlights</h4>
                    <div className="highlights-tags">
                      {hotel.guestReview.highlights.map((highlight, idx) => (
                        <span key={idx} className="highlight-tag">
                          ✓ {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.section>
              )}

              {/* Why Stay Here */}
              <motion.section
                className="detail-section"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2>Why Stay Here</h2>
                <div className="highlights-list">
                  {hotel.highlights.map((highlight, idx) => (
                    <div key={idx} className="highlight-item">
                      <span className="highlight-number">{idx + 1}</span>
                      <span className="highlight-text">{highlight}</span>
                    </div>
                  ))}
                </div>
              </motion.section>
            </div>

            {/* Sidebar */}
            <aside className="hotel-sidebar">
              {/* Share Card */}
              <motion.div
                className="share-card"
                initial={{ x: 20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3>Share This Hotel</h3>
                <div className="share-buttons">
                  <button 
                    className="share-btn" 
                    onClick={handleShare}
                    aria-label="Share this hotel"
                  >
                    <FaShare /> Share
                  </button>
                  
                  {shareMessage && (
                    <div className="share-message">{shareMessage}</div>
                  )}
                </div>
              </motion.div>

              {/* Floating Book Now Button */}
              <div className="floating-book-now">
                <a
                  href={hotel.bookingUrl || '#'}
                  target={hotel.bookingUrl ? "_blank" : "_self"}
                  rel={hotel.bookingUrl ? "noopener noreferrer" : ""}
                  className="floating-btn book-now-floating"
                  onClick={!hotel.bookingUrl ? (e) => { 
                    e.preventDefault(); 
                    alert('Booking information will be available soon. Please contact us directly.'); 
                  } : undefined}
                >
                  <FaExternalLinkAlt /> Book Now
                </a>
              </div>

              {/* Quick Info Card */}
              <motion.div
                className="quick-info-card"
                initial={{ x: 20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h3>Quick Info</h3>

                <div className="info-item">
                  <span className="info-label">Price Range</span>
                  <span className="info-value">{hotel.priceRange}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Best Month to Visit</span>
                  <span className="info-value">{hotel.bestMonth}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Rating</span>
                  <span className="info-value">
                    <FaStar className="star-icon" /> {hotel.rating}/5
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">Phone</span>
                  <a href={`tel:${hotel.contact}`} className="info-value link">
                    {hotel.contact}
                  </a>
                </div>

                <div className="booking-tip">
                  <h4>💡 Booking Tip</h4>
                  <p>{hotel.bookingTip}</p>
                </div>
              </motion.div>

              {/* Room Types Card */}
              <motion.div
                className="room-types-card"
                initial={{ x: 20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3>Room Types</h3>
                <div className="room-types-list">
                  {hotel.roomTypes.map((room, idx) => (
                    <div key={idx} className="room-type-item">
                      <span className="room-bullet">•</span>
                      <span>{room}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              )}
            </aside>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HotelDetail;
